import React, { Fragment, useRef, useContext, useMemo, useEffect } from "react";

import { isEmpty, recoverTag } from "bodycodi/cmap/app/js/common";
import down from "bodycodi/cmap/app/img/down.png";
import up from "bodycodi/cmap/app/img/up.png";
import { DispatchContext } from "bodycodi/cmap/app/components/common/context";
import icon_question_small from "bodycodi/cmap/app/svg/icon_question_small.svg";

interface PassProp {
  d: any;
  isAdd: boolean;
  open?: boolean;
  setOpen?: any;
  isClassCheck?: boolean;
  isClassTab?: boolean;
  member?: any;
  onClick?: Function;
  swiperWrap?: any;
}

// in: '2021-06-29'
// out: '2021.6.29'
const changeDateFormat = (date = "2000-01-01"): string => {
  return (
    date.split("-")[0] +
    "." +
    parseInt(date.split("-")[1], 10) * 1 +
    "." +
    parseInt(date.split("-")[2], 10) * 1
  );
};

const Pass = ({
  d,
  member,
  onClick,
  swiperWrap,
  isAdd,
  isClassCheck = false,
  isClassTab = false,
  open,
  setOpen,
}: PassProp) => {
  const { dispatch } = useContext(DispatchContext);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const foldWrap = useRef<HTMLDivElement>(null);
  const fold = useRef<HTMLDivElement>(null);
  const passName =
    d.serviceName +
    " " +
    d.usePeriod +
    "" +
    (d.usePeriodType === "M" ? "개월" : "일") +
    " " +
    (isEmpty(d.useNumber) ? "" : d.useNumber * 1 > 0 ? d.useNumber + "회" : "");

  const remainDayLimit = d.dayLimit - d.dayScheduleCnt;
  const remainWeekLimit = d.weekLimit - d.weekScheduleCnt;
  const remainMaxBookingNumber = d.maxBookingNumber - d.reservationScheduleCnt;

  const isAvailablePass = useMemo(() => {
    if (isAdd) {
      if (!member.selectedSeqPassInfo) return false;
      return d.seqPassInfo === member.selectedSeqPassInfo;
    } else {
      return (
        (d.appointmentMemberYn === "Y" && d.remainNumber !== d.reservationScheduleCnt) ||
        d.serviceType === "CLASS" ||
        d.serviceType === "PLACE" ||
        d.serviceType === "OPTION"
      );
    }
  }, [d, member]);

  useEffect(() => {
    if (!open) {
      foldWrap.current!.style.height = "0px";
    } else {
      if (swiperWrap) {
        swiperWrap.current.querySelector(
          ".swiper-container-autoheight .swiper-wrapper"
        ).style.height = "auto";
      }
      foldWrap.current!.style.height = fold.current!.scrollHeight + "px";
    }
  }, [open]);

  const clickOpen = () => {
    setOpen(!open);
  };

  const clickPass = () => {
    if (onClick) {
      onClick(member, d, d.seqPassInfo === member.selectedSeqPassInfo);
    }
  };

  const handleClickInfo = (type: string) => {
    dispatch({
      type: "POP_UP_OPEN",
      id: "PASS_CARD_INFO_POP",
      open: { passCardInfoPop: true },
    });

    dispatch({
      type: "UPDATE",
      data: { passCardInfoType: type },
    });
  };

  return (
    <>
      <div
        className={`pass-wrapper ${isAvailablePass && "active"} ${
          window?.innerWidth < 375 ? "width" : ""
        }`}
        ref={wrapperRef}
      >
        {d.status === "PAUSE" && (
          <div className="pass-pause-wrapper">
            <span className="pass-pause-title">중지기간</span>
            <span className="pass-pause-period">
              {changeDateFormat(d.pausedPassInfoStartDt)} -{" "}
              {changeDateFormat(d.pausedPassInfoEndDt)}
            </span>
          </div>
        )}
        <div className={`pass-inner-wrapper ${d.status === "AVAILABLE" ? "" : "disable"}`}>
          <div onClick={clickPass}>
            <div className="pass-name">
              {d?.packagePass === "Y" && <div className="package">패키지</div>}
              <div className="black">{recoverTag(passName)}</div>
              {d.serviceType === "APPOINTMENT" && <div className="time">{d.serviceTime}분</div>}
            </div>
            <div className="pass-date">
              {changeDateFormat(d.useStrtDate)} - {changeDateFormat(d.useEndDate)}
              <span className="middot">&#183;</span>
              <span className={d.remainDate <= 7 ? "red" : ""}>{d.remainDate}일남음</span>
            </div>

            <div className="pass-remain-date">
              {d.serviceKind === "P" && (
                <>
                  <div className="pass-remain-count">
                    <span className="gray">잔여일수 </span>
                    <div>
                      <span className="blue">{d.remainDate}일 </span>
                      <span className="slash"> | </span>
                      <span className="black">총 {d.useDate}일</span>
                    </div>
                  </div>
                  <div className="pass-info-bar">
                    <span className="blue">예약 {d.reservationScheduleCnt}</span>
                    <span className="green">출석 {d.attendenceCnt}</span>
                    <span className="red">결석 {d.absentCnt}</span>
                  </div>
                </>
              )}
              {d.serviceKind === "N" && (
                <>
                  <div className="pass-remain-count">
                    <span className="gray">잔여횟수 </span>
                    <div>
                      <span className="blue">{d.remainNumber}회 </span>
                      <span className="slash"> | </span>
                      <span className="black">총 {d.useNumber}회</span>
                    </div>
                  </div>
                  <div className="pass-info-bar">
                    <span className="blue">예약 {d.reservationScheduleCnt}</span>
                    <span className="green">출석 {d.attendenceCnt}</span>
                    <span className="red">결석 {d.absentCnt}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div
            className={"pass-fold-wrap" + (open ? " open" : "")}
            ref={foldWrap}
            onClick={clickPass}
          >
            <div className="pass-fold" ref={fold}>
              <ul>
                <li>
                  <div className="title">주간 이용</div>
                  <div className="decs">
                    {d.weekLimit * 1 === -1 && <span className="bold blue">무제한</span>}
                    {d.weekLimit * 1 !== -1 && (
                      <Fragment>
                        <span className="blue">
                          {remainWeekLimit} {d.weekLimitDayFlag === "D" ? "일" : "회"} 남음
                        </span>
                        <span className="gray"> | </span>
                        <span className="black">
                          주 {d.weekLimit} {d.weekLimitDayFlag === "D" ? "일" : "회"}
                        </span>
                      </Fragment>
                    )}
                  </div>
                </li>
                <li>
                  <div className="title">1일 이용</div>
                  <div className="decs">
                    {d.dayLimit * 1 === -1 && <span className="bold blue">무제한</span>}
                    {d.dayLimit * 1 !== -1 && (
                      <Fragment>
                        <span className="blue">{remainDayLimit}회 남음</span>
                        <span className="gray"> | </span>
                        <span className="black">일 {d.dayLimit}회</span>
                      </Fragment>
                    )}
                  </div>
                </li>
                <li>
                  <div className="title">
                    최대 예약권{" "}
                    <img
                      src={icon_question_small}
                      alt=""
                      onClick={() => handleClickInfo("maxBooking")}
                    />
                  </div>
                  <div className="decs">
                    {d.maxBookingNumber * 1 === -1 && <span className="bold blue">무제한</span>}
                    {d.maxBookingNumber * 1 !== -1 && (
                      <Fragment>
                        <span className="blue">{remainMaxBookingNumber}회 남음</span>
                        <span className="gray"> | </span>
                        <span className="black">총 {d.maxBookingNumber}회</span>
                      </Fragment>
                    )}
                  </div>
                </li>
                <li>
                  <div className="title">
                    예약 취소권{" "}
                    <img
                      src={icon_question_small}
                      alt=""
                      onClick={() => handleClickInfo("cancel")}
                    />
                  </div>
                  <div className="decs">
                    {d.cancelNumber * 1 === -1 && <span className="bold blue">무제한</span>}
                    {d.cancelNumber * 1 !== -1 && (
                      <Fragment>
                        <span className="blue">{d.remainCancelNumber}회 남음</span>
                        <span className="gray"> | </span>
                        <span className="black">총 {d.cancelNumber}회</span>
                      </Fragment>
                    )}
                  </div>
                </li>
                {d?.forceCancelNumber !== 0 && (
                  <li>
                    <div className="title">
                      특별 취소권{" "}
                      <img
                        src={icon_question_small}
                        alt=""
                        onClick={() => handleClickInfo("forceCancel")}
                      />
                    </div>
                    <div className="decs">
                      {d.forceCancelNumber * 1 === -1 && <span className="bold blue">무제한</span>}
                      {d.forceCancelNumber * 1 !== -1 && (
                        <Fragment>
                          <span className="blue">{d.remainForceCancelNumber}회 남음</span>
                          <span className="gray"> | </span>
                          <span className="black">총 {d.forceCancelNumber}회</span>
                        </Fragment>
                      )}
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {!isClassCheck && (
            <div className="pass-rsrv">
              <span className="more" onClick={clickOpen}>
                {open && (
                  <Fragment>
                    접기
                    <img src={up} />
                  </Fragment>
                )}
                {!open && (
                  <Fragment>
                    더보기
                    <img src={down} />
                  </Fragment>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Pass;
